import { Fragment, useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { LinjaLinkki } from "./Linkit";
import { api, punchinLuokka, punchinTeksti, punchSuljettu, type PunchRivi, type Kayttaja, type HoitajaSuodatin } from "./api";
import { Hoitaja } from "./Hoitaja";
import Liitteet from "./Liitteet";
import { Tilasana } from "./Tila";
import { t } from "./kieli";
import { luokanNimi, luokanMaar } from "./Punchit";

/* Punch-selailu: kaikki tarkastuslöydökset yli linjojen. Avoimet ensin.
   Kirjaaja voi kuitata (Korjattu) suoraan. Read-painotteinen yleisnäkymä.

   `vainAvoimet` on Selailun tilaa eikä tämän: se on osoitteessa
   (`?nayta=punchit&kohde=&vain_avoimet=1`), koska Projekti-Hubin
   "N avointa punchia" osoittaa tänne ja F5:n on palautettava sama
   joukko. Otsikon luku on palvelimen `yhteensa` (ikkunafunktio ennen
   LIMITiä), ei rivien määrä — ja `data-rivit` on kävelytarkistuksen
   sopimus: Hubin `data-luku` verrataan tähän. */

export default function PunchSelailu({ kohde, saaKirjata, saaPoistaaLiitteen = false, vainAvoimet, asetaVainAvoimet,
  kayttaja, hoitaja, asetaHoitaja }: {
  kohde: number | ""; saaKirjata: boolean;
  /** DELETE /api/liitteet/:id on tiukempi kuin lisäys (pääkäyttäjä, suunnittelija). */
  saaPoistaaLiitteen?: boolean;
  vainAvoimet: boolean; asetaVainAvoimet: (v: boolean) => void;
  /** 236 (T534): kuka hoitaa — suodatin osoitteessa (Selailun tila). */
  kayttaja: Kayttaja; hoitaja: HoitajaSuodatin | ""; asetaHoitaja: (h: HoitajaSuodatin | "") => void;
}) {
  // Toni 17.10.2026 klo 07.40 kohta 4: kenttätekijä saa olla hoitaja (Ota-nappi) ja lisätä kuvan hoitamaansa punchiin; Korjattu ei.
  const kenttatekija = kayttaja.roolit.includes("kenttatekija");
  const toimet = saaKirjata || kenttatekija;
  const [rivit, setRivit] = useState<PunchRivi[] | null>(null);
  const [haku, setHaku] = useState("");
  /* Punchin kuvat: liite jonka kohde_taulu = 'punch'. Sama mekanismi
     kuin tarkastuspöytäkirjalla — ei toista kuvamekanismia. */
  const [kuvatAuki, setKuvatAuki] = useState<number | null>(null);

  const lataa = useCallback(() => {
    api.kaikkiPunchit({ ...(kohde !== "" && { kohde }), vain_avoimet: vainAvoimet, ...(hoitaja !== "" && { hoitaja }) })
      .then(setRivit).catch(() => setRivit([]));
  }, [kohde, vainAvoimet, hoitaja]);
  useEffect(() => { lataa(); }, [lataa]);

  const naytettavat = (rivit ?? []).filter((p) => {
    if (!haku.trim()) return true;
    const h = haku.toLowerCase();
    return (p.linja ?? "").toLowerCase().includes(h) || (p.vika ?? "").toLowerCase().includes(h);
  });
  const avoimia = (rivit ?? []).filter((p) => !punchSuljettu(p)).length;
  /* Suodatuksen koko, ei sivun. Tyhjä lista on nolla, ei tuntematon:
     palvelin vastasi eikä yksikään rivi osunut. */
  const yhteensa = rivit === null ? null : (rivit[0]?.yhteensa ?? 0);

  async function kuittaa(id: number) {
    await api.kuittaaPunch(id);
    lataa();
  }

  return (
    <div data-punch-selailu="">
      <h3>
        {t("psel.otsikko")} ·{" "}
        {yhteensa === null
          ? "…"
          : <span data-rivit={yhteensa} data-joukko="punchit">{yhteensa}</span>}
        {vainAvoimet ? ` ${t("psel.avointa")}` : ""}
        {haku.trim() && rivit ? ` · ${t("psel.naytetaan", { n: naytettavat.length })}` : ""}
      </h3>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", margin: "2px 0 12px" }}>
        <input value={haku} onChange={(e) => setHaku(e.target.value)} placeholder={t("psel.haku")} style={{ minWidth: 220 }} />
        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={vainAvoimet} onChange={(e) => asetaVainAvoimet(e.target.checked)} />
          {t("psel.vain_avoimet")}
        </label>
        <label style={{ fontSize: 13 }}>{t("hoito.suodatin")}{" "}
          <select value={hoitaja} data-suodatin="hoitaja" onChange={(e) => asetaHoitaja(e.target.value as HoitajaSuodatin | "")}>
            <option value="">{t("pu.kaikki")}</option>
            <option value="minun">{t("hoito.suodatin.minun")}</option>
            <option value="ei">{t("hoito.suodatin.ei")}</option>
          </select>
        </label>
        {rivit && !vainAvoimet && <span className="dim" >{t("psel.avoimia_listalla", { n: avoimia })}</span>}
      </div>

      {rivit === null && <p className="dim">{t("ltar.ladataan")}</p>}
      {rivit && naytettavat.length === 0 && <p className="dim">{t("psel.ei_loydoksia")}</p>}
      {naytettavat.length > 0 && (
        <div className="scroll">
          <table className="tbl" style={{ fontSize: 13 }}>
            <thead><tr>
              <th>{t("nippu.linja")}</th><th>{t("hl.hitsi")}</th><th>{t("psel.luokka")}</th><th>{t("psel.vika")}</th><th>{t("psel.huomioinut")}</th><th>{t("ltar.pvm")}</th><th>{t("nippu.tila")}</th><th>{t("pu.kuvat")}</th>
              {toimet && <th></th>}
            </tr></thead>
            <tbody>
              {naytettavat.map((p) => (
                <Fragment key={p.id}><tr>
                  {/* Linja on linkki: sama osoite kuin Selailun oma polku
                      (#/selailu/<tunnus>), joten F5 palauttaa sen ja linkin voi
                      lähettää. Kohdesuodatin jää voimaan — jos linja kuuluu
                      toiseen kohteeseen, Selailu sanoo sen eikä arvaa. */}
                  <td className="mono"><LinjaLinkki tunnus={p.linja} title={t("psel.avaa_linja")} /></td>
                  <td className="mono">{p.hitsinumero ?? ""}</td>
                  <td>{(() => { const lk = punchinLuokka(p.luokka); return lk
                    ? <span className={lk.tunnus === "A" ? "merkki merkki--vika" : "merkki"} title={luokanMaar(lk.tunnus, lk.maaritelma)}>{lk.tunnus} · {luokanNimi(lk.tunnus, lk.nimi)}</span>
                    : <span className="dim">—</span>; })()}</td>
                  <td>{punchinTeksti(p)}{p.isantajarjestelma === "sharepoint" && <span className="dim" > · SP</span>}
                    {/* Toni 17.10.2026 klo 07.40 kohta 2: vastuu (kuka korjaa) näkyviin. */}
                    {p.vastuu && <div className="dim" data-punch-vastuu-nakyy={p.id}>{t("pc.vastuu_lyhyt")}: {p.vastuu}</div>}</td>
                  <td>{p.huomioinut ?? ""}</td>
                  <td>{p.huomio_pvm ? naytaPvm(p.huomio_pvm) : ""}</td>
                  <td>{punchSuljettu(p)
                    ? <Tilasana laji="kunnossa" >{t("psel.korjattu")} {p.kuittaus_pvm ? naytaPvm(p.kuittaus_pvm) : ""}{!p.kuitannut && <span className="dim"> · {t("psel.kuittaaja_ei_tiedossa")}</span>}</Tilasana>
                    : <Tilasana laji="vika" style={{ fontSize: 12 }}>{t("psel.avoin")}</Tilasana>}</td>
                  <td>
                    <button className="nappi nappi--kevyt" onClick={() => setKuvatAuki(kuvatAuki === p.id ? null : p.id)}
                            title={t("pc.kuvat_title")}>
                      {kuvatAuki === p.id ? t("hl.sulje") : (p.liitteita ? t("hl.kuvat_n", { n: p.liitteita }) : (saaKirjata || (kenttatekija && p.hoitaja?.id === kayttaja.id) ? t("hl.lisaa_kuva") : "—"))}
                    </button>
                  </td>
                  {toimet && (
                    <td style={{ whiteSpace: "nowrap" }}>
                      {/* 236 (T534): hoitajan napit omalla rivillään Korjattu-napin yläpuolella — samalla rivillä Korjattu jäi 1366 px:ssä näkyvän alueen ulkopuolelle (13.10.2026). */}
                      {(!punchSuljettu(p) || p.hoitaja) && (<div>
                        <Hoitaja taulu="punch" id={p.id} hoitaja={p.hoitaja} kayttaja={kayttaja} muuttui={lataa}
                                 saaKirjoittaa={!punchSuljettu(p)} />
                      </div>)}
                      {saaKirjata && !punchSuljettu(p) && (
                      <button className="nappi nappi--kevyt" onClick={() => void kuittaa(p.id)}>{t("pc.korjattu")}</button>
                    )}</td>
                  )}
                </tr>
                {kuvatAuki === p.id && (
                  <tr>
                    <td colSpan={toimet ? 9 : 8}>
                      <Liitteet pienoiskuvat kohdeTaulu="punch" kohdeId={p.id} saaMuokata={saaKirjata || (kenttatekija && p.hoitaja?.id === kayttaja.id)}
                                saaPoistaa={saaPoistaaLiitteen} otsikko={t("pu.kuvat")} muuttui={lataa} />
                    </td>
                  </tr>
                )}</Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
